import { createContext, useCallback, useContext, useState } from "react";
import { useAuth } from "./auth";
import LoginModal from "../components/LoginModal";

/**
 * useAuthGate — one hook to wrap any auth-gated action.
 *
 *   const { requireAuth, openLogin } = useAuthGate();
 *   <button onClick={requireAuth(() => api.post("/likes"), "Log in to like posts.")}>❤</button>
 *
 * If the visitor is already authed, the callback runs immediately.
 * If not, we stash the callback, open the global LoginModal, and — on
 * successful auth — replay the callback so the user's original intent isn't
 * lost. The `reason` string is optional context copy shown at the top of
 * the modal ("Log in to comment", "Sign up to apply for this job", etc.).
 *
 * The provider (<AuthGateProvider>) must sit inside <AuthProvider> so
 * `useAuth()` is available.
 */
const AuthGateCtx = createContext(null);

export function AuthGateProvider({ children }) {
  const { user } = useAuth();
  const [modal, setModal] = useState({ open: false, reason: "", pending: null });

  const openLogin = useCallback((reason = "") => {
    setModal({ open: true, reason, pending: null });
  }, []);

  const requireAuth = useCallback((cb, reason = "") => {
    return (...args) => {
      if (user) return cb(...args);
      setModal({ open: true, reason, pending: () => cb(...args) });
    };
  }, [user]);

  // ensureAuth — run cb NOW if the visitor is signed in, else open the
  // LoginModal and queue cb for after auth. This is the imperative variant
  // of requireAuth (which returns a wrapped handler).
  const ensureAuth = useCallback((cb, reason = "") => {
    if (user) return cb();
    setModal({ open: true, reason, pending: cb });
  }, [user]);

  const onClose = useCallback((didAuth) => {
    setModal((m) => {
      // Fire-and-forget the pending callback on successful sign-in.
      if (didAuth && m.pending) {
        // Defer to next tick so React can flush the new `user` from auth.
        setTimeout(() => m.pending(), 50);
      }
      return { open: false, reason: "", pending: null };
    });
  }, []);

  return (
    <AuthGateCtx.Provider value={{ requireAuth, ensureAuth, openLogin }}>
      {children}
      <LoginModal open={modal.open} reason={modal.reason} onClose={onClose} />
    </AuthGateCtx.Provider>
  );
}

export const useAuthGate = () => useContext(AuthGateCtx) || {
  // Safety-net stub in case a component renders outside the provider —
  // prevents crashes on transitional pages.
  requireAuth: (cb) => cb,
  ensureAuth: (cb) => cb?.(),
  openLogin: () => {},
};
